// ResponsiveNotes.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';


function normalizeNotes(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function NotesList({ notes: arr, DateFormat }) {
  const notes = normalizeNotes(arr);
  if (!notes.length) return <div className="text-gray-400">No notes</div>;
  return (
    <ul className="space-y-3">
      {notes.map((n) => (
        <li key={n.id}>
          <div className="text-sm whitespace-pre-wrap">{n.notes}</div>
          <div className="text-xs text-gray-500">
            — {n.employee_name} · {DateFormat ? <DateFormat date={n.date} /> : n.date}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function ResponsiveNotes({
  notes,               // array or JSON string
  DateFormat,          // optional component for dates
  onOpenModal,         // fn to open a modal with all notes
  rowId,               // id for this row (used by parent)
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  // Observe width changes of the cell
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect?.width ?? 0;
        setWidth(w);
      }
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Simple width breakpoints → how many notes and chars to show
  const config = useMemo(() => {
    if (width >= 900) return { maxNotes: Infinity, maxChars: Infinity, compact: false };
    if (width >= 700) return { maxNotes: 3, maxChars: 280, compact: true };
    if (width >= 500) return { maxNotes: 2, maxChars: 200, compact: true };
    return { maxNotes: 1, maxChars: 100, compact: true };
  }, [width]);

  const notesArr = normalizeNotes(notes);

  // Build preview according to config
  const { previewItems, hasMore } = useMemo(() => {
    if (!notesArr.length) return { previewItems: [], hasMore: false };

    const sliceCount = Number.isFinite(config.maxNotes) ? config.maxNotes : notesArr.length;
    const head = notesArr.slice(0, sliceCount).map((n) => {
      const text = String(n.notes || '');
      const truncated =
        Number.isFinite(config.maxChars) && text.length > config.maxChars
          ? text.slice(0, config.maxChars) + '...'
          : text;
      return { ...n, _preview: truncated, _truncated: truncated !== text };
    });

    const moreBecauseOfCount = notesArr.length > sliceCount;
    const moreBecauseOfTrunc = head.some(h => h._truncated);
    return { previewItems: head, hasMore: moreBecauseOfCount || moreBecauseOfTrunc };
  }, [notesArr, config]);

  // Expanded mode: show full list (no “more”)
  if (!config.compact) {
    return (
      <div ref={containerRef}>
        <NotesList notes={notesArr} DateFormat={DateFormat} />
      </div>
    );
  }

  // Compact mode: show limited preview + “more” if needed
  return (
    <div ref={containerRef}>
      {previewItems.length > 0 && (
        <ul className="p-1">
          {previewItems.map((n) => (
            <li key={n.id}>
              <div className="text-xs text-blue-500">
                — {n.employee_name} · {n.date}
              </div>              
              <div className="text-sm whitespace-pre-wrap">{n._preview}</div>
              {/* user & date ALWAYS visible outside of the truncated text */}
            </li>
          ))}
        </ul>
      )}

      {/* {hasMore && (
        <button
          type="button"
          onClick={() => onOpenModal?.(rowId)}
          className="mt-1 text-indigo-800 hover:text-indigo-800"
          title="View all notes"
        >
          <EllipsisHorizontalIcon className="w-5 h-5 inline-block text-blue-800" />
        </button>
      )} */}

    </div>
  );
}
